// src/components/CompanyResourcesPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import CompanyService from '../services/companyService';
import CompanyResourceService from '../services/companyResourceService';
import Button from './Button.jsx';
import axios from 'axios';
import md5 from 'md5';
import { message } from 'antd';
import { useAuth } from '../auth.js';
import {
  folderUploadErrorMessage,
  resolveSharePointAccessTokenForUpload,
} from '../utils/sharepointFolderUpload.js';

const TYPE_OPTIONS = ['drive_folder', 'sharepoint_folder', 'dropbox_folder', 'link', 'doc'];

function getLinkType(url) {
  if (!url) return 'other';
  if (url.includes('drive.google.com')) return 'google';
  if (url.includes('sharepoint.com') || url.includes('1drv.ms') || url.includes('onedrive.live.com')) return 'sharepoint';
  if (url.includes('dropbox.com')) return 'dropbox';
  return 'other';
}

export default function CompanyResourcesPage() {
  const auth = useAuth();

  const currentUserId = auth.user?.sub || null;

  const currentUserEmail = String(
    auth.user?.profile?.email || auth.user?.email || ''
  )
    .trim()
    .toLowerCase() || null;
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState(null);
  const [items, setItems] = useState([]);
  const [nextToken, setNextToken] = useState(null);
  const [error, setError] = useState('');

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [draft, setDraft] = useState({
    name: '',
    type: 'drive_folder',
    url: '',
  });

  const companyId = company?.id;

  const canCreate = useMemo(() => !!companyId, [companyId]);

  async function loadCompanyAndResources() {
    setLoading(true);
    setError('');
    try {
      const payload = await CompanyService.getMyCompany();
      const c = payload?.company ?? payload ?? null;
      setCompany(c);

      if (!c?.id) {
        setItems([]);
        setNextToken(null);
        return;
      }

      const res = await CompanyResourceService.listByCompany({ companyId: c.id, limit: 50 });

      const safeItems = Array.isArray(res?.items) ? res.items.filter(Boolean) : [];
      setItems(safeItems);

      setNextToken(res?.nextToken || null);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadCompanyAndResources();
  }, []);

  async function loadMore() {
    if (!companyId || !nextToken) return;
    try {
      const res = await CompanyResourceService.listByCompany({ companyId, limit: 50, nextToken });

      const nextItems = Array.isArray(res?.items) ? res.items.filter(Boolean) : [];
      setItems(prev => [...(Array.isArray(prev) ? prev.filter(Boolean) : []), ...nextItems]);

      setNextToken(res?.nextToken || null);
    } catch (e) {
      setError(e?.message || String(e));
    }
  }

  async function handleCreate() {
    if (!companyId) return;

    const input = {
      companyId,
      name: draft.name.trim(),
      type: draft.type,
      url: draft.url.trim(),
    };

    if (!input.name) {
      setError('name is required');
      return;
    }

    if (!input.url) {
      setError('url is required');
      return;
    }

    try {
      setError('');
      const created = await CompanyResourceService.create(input);
      setItems(prev => [created, ...(prev || [])]);
      setIsCreateOpen(false);
      setDraft({ name: '', type: 'drive_folder', url: '' });
    } catch (e) {
      setError(e?.message || String(e));
    }
  }

  async function handleDelete(id) {
    if (!id) return;
    if (!window.confirm('delete this resource?')) return;

    try {
      setError('');
      await CompanyResourceService.remove(id);
      setItems(prev => (prev || []).filter(x => x?.id !== id));
    } catch (e) {
      setError(e?.message || String(e));
    }
  }

  async function handleInlineUpdate(id, patch) {
    if (!id) return;
    try {
      setError('');
      const updated = await CompanyResourceService.update({ id, ...patch });
      setItems(prev => (prev || []).map(x => (x?.id === id ? updated : x)));
    } catch (e) {
      setError(e?.message || String(e));
    }
  }

  async function handleSyncFiles(item) {
    console.log('handleSyncFiles called with item:', item);
    console.log('REACT_APP_BACKEND_URL_UPLOAD_COMPANY:', process.env.REACT_APP_BACKEND_URL_UPLOAD_COMPANY);
    const folderUrl = (item?.url || '').trim();
    if (!companyId || !item?.id) {
      setError('missing companyId or resourceId');
      return;
    }
    if (!folderUrl) {
      setError('resource url is empty');
      return;
    }
    if (!currentUserId || !currentUserEmail) {
      setError('missing userId/userEmail');
      return;
    }

    // stable link id for this resource url
    const linkId = md5(`${folderUrl}_${companyId}_${item.id}`);

    // MUST match your lambda’s jobId convention
    const jobId = `co#${companyId}#${item.id}#${linkId}`;

    const idToken = localStorage.getItem('id_token');

    let sharepointAccessToken;
    const linkType = getLinkType(folderUrl);
    if (linkType === 'sharepoint') {
      try {
        sharepointAccessToken = await resolveSharePointAccessTokenForUpload(folderUrl, idToken);
      } catch (e) {
        const msg = e?.message || String(e);
        setError(msg);
        message.error(msg);
        return;
      }
    }

    try {
      setError('');
      message.loading('Starting upload…', 1);

      const uploadUrl = `${process.env.REACT_APP_BACKEND_URL_UPLOAD_COMPANY}/uploadCompanyResourceFolder`;

      console.log('About to call uploadCompanyResourceFolder', {
        url: uploadUrl,
        body: {
          companyId,
          resourceId: item.id,
          folderUrl,
          linkId,
          jobId,
          userId: currentUserId,
          userEmail: currentUserEmail,
          sharepointAccessToken,
        },
        hasIdToken: !!idToken,
      });

      const resp = await axios.post(
        uploadUrl,
        {
          companyId,
          resourceId: item.id,
          folderUrl,
          linkId,
          jobId,
          userId: currentUserId,
          userEmail: currentUserEmail,
          sharepointAccessToken,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
          },
        }
      );

      if (resp.status === 200 || resp.status === 202) {
        message.success('Upload started. Chunking job created.');
      } else {
        message.warning(`Upload call returned ${resp.status}`);
      }
    } catch (e) {
      console.error(e);
      const msg = folderUploadErrorMessage(e, 'Failed to start upload.');
      setError(msg);
      message.error(msg);
    }
  }

  if (loading) {
    return <Wrap><h2>company resources</h2><p>loading…</p></Wrap>;
  }

  return (
    <Wrap>
      <HeaderRow>
        <div>
          <Title>company resources</Title>
          <Sub>
            company: <b>{company?.name || company?.id || 'none'}</b>
          </Sub>
        </div>

        <HeaderActions>
          <Button onClick={loadCompanyAndResources}>refresh</Button>
          <Button disabled={!canCreate} onClick={() => setIsCreateOpen(true)}>
            add resource
          </Button>
        </HeaderActions>
      </HeaderRow>

      {!!error && <ErrorBox>{error}</ErrorBox>}

      {isCreateOpen && (
        <Card>
          <CardTitle>add resource</CardTitle>

          <Grid>
            <Field>
              <Label>type</Label>
              <Select value={draft.type} onChange={e => setDraft(d => ({ ...d, type: e.target.value }))}>
                {TYPE_OPTIONS.map(x => <option key={x} value={x}>{x}</option>)}
              </Select>
            </Field>

            <Field>
              <Label>name</Label>
              <Input
                placeholder="enter resource name"
                value={draft.name}
                onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
              />
            </Field>

            <Field style={{ gridColumn: '1 / -1' }}>
              <Label>folder/link url</Label>
              <Input
                placeholder="paste google drive folder link / sharepoint folder link / etc"
                value={draft.url}
                onChange={e => setDraft(d => ({ ...d, url: e.target.value }))}
              />
            </Field>
          </Grid>

          <Row>
            <Button onClick={() => {
              setIsCreateOpen(false);
              setDraft({ name: '', type: 'drive_folder', url: '' });
            }}>
              cancel
            </Button>
            <Button onClick={handleCreate}>create</Button>
          </Row>
        </Card>
      )}

      <List>
        {(Array.isArray(items) ? items.filter(Boolean) : []).map((item, idx) => (
  <       ItemCard key={item?.id || item?.url || `row-${idx}`}>
            <ItemTop>
              <div>
                <ItemMeta>
                  <Tag>{item.type || 'link'}</Tag>
                  <Small>{item.createdAt ? new Date(item.createdAt).toLocaleString() : ''}</Small>
                </ItemMeta>
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <Button onClick={() => handleSyncFiles(item)}>sync files</Button>
                <Button onClick={() => handleDelete(item.id)}>delete</Button>
              </div>
            </ItemTop>

            <ItemUrl>
              <a href={item.url} target="_blank" rel="noreferrer">{item.url}</a>
            </ItemUrl>
          </ItemCard>
        ))}
      </List>

      {!!nextToken && (
        <LoadMoreRow>
          <Button onClick={loadMore}>load more</Button>
        </LoadMoreRow>
      )}
    </Wrap>
  );
}

const Wrap = styled.div`
  padding: 24px;
  max-width: 1100px;
  margin: 0 auto;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 10px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 22px;
  letter-spacing: 0.2px;
`;

const Sub = styled.div`
  margin-top: 4px;
  opacity: 0.75;
  font-size: 13px;
`;

const ErrorBox = styled.div`
  background: rgba(229, 62, 62, 0.08);
  border: 1px solid rgba(229, 62, 62, 0.25);
  color: #e53e3e;
  padding: 10px 12px;
  border-radius: 10px;
  margin-bottom: 16px;
`;

const Card = styled.div`
  border: 1px solid rgba(0,0,0,0.12);
  border-radius: 14px;
  padding: 14px;
  margin-bottom: 18px;
`;

const CardTitle = styled.div`
  font-weight: 600;
  margin-bottom: 10px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 220px;
  gap: 12px;
`;

const Field = styled.div``;

const Label = styled.div`
  font-size: 12px;
  opacity: 0.75;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  border: 1px solid rgba(0,0,0,0.15);
  border-radius: 10px;
  padding: 10px 12px;
  outline: none;
`;

const Select = styled.select`
  width: 100%;
  border: 1px solid rgba(0,0,0,0.15);
  border-radius: 10px;
  padding: 10px 12px;
  outline: none;
`;

const TextArea = styled.textarea`
  width: 100%;
  border: 1px solid rgba(0,0,0,0.15);
  border-radius: 10px;
  padding: 10px 12px;
  outline: none;
  resize: vertical;
`;

const Row = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 12px;
`;

const List = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
`;

const ItemCard = styled.div`
  border: 1px solid rgba(0,0,0,0.12);
  border-radius: 14px;
  padding: 14px;
`;

const ItemTop = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 14px;
`;

const ItemName = styled.div`
  font-size: 16px;
  font-weight: 600;
  outline: none;
`;

const ItemMeta = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  margin-top: 6px;
`;

const Tag = styled.div`
  font-size: 12px;
  padding: 2px 10px;
  border-radius: 999px;
  border: 1px solid rgba(0,0,0,0.15);
  opacity: 0.8;
`;

const Small = styled.div`
  font-size: 12px;
  opacity: 0.7;
`;

const ItemUrl = styled.div`
  margin-top: 10px;
  font-size: 13px;
  a { word-break: break-all; }
`;

const ItemDesc = styled.div`
  margin-top: 10px;
  font-size: 13px;
  opacity: 0.9;
  outline: none;
`;

const LoadMoreRow = styled.div`
  margin-top: 16px;
  display: flex;
  justify-content: center;
`;